/**
 * Signed-out welcome push prompt (native app only).
 *
 * Fires on first launch for a signed-out visitor on the native app, so
 * we can capture push opt-in at the highest-intent moment (fresh app
 * open) BEFORE they hit the signup wall. There is no userId yet, so
 * the token is not registered here; instead the user's choice is
 * stashed in localStorage and picked up by the post-auth register
 * hook once they sign up.
 *
 * Never shows in a browser (per src/lib/push.ts pushSupported() the
 * web push path is disabled), never shows for signed-in users (the
 * signed-in PushPromptModal handles that), never shows if the user
 * has already answered (granted / denied) at this venue.
 */
import { useEffect, useState } from "react";
import { isNativeApp } from "../lib/platform";
import { requestNativePushPermission } from "../lib/push";
import {
  readPreAuthPushState,
  shouldShowWelcomePrompt,
  writePreAuthPushState,
  type PreAuthPushResponse,
} from "../lib/preauth-push-state";

type Props = {
  venueId: string;
  venueName?: string;
  /** True iff user is currently signed OUT. Signed-in users are
   *  handled by <PushPromptModal>, not here. */
  signedOut: boolean;
};

export function WelcomePushPrompt({ venueId, venueName, signedOut }: Props) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Self-hide the moment signedOut flips false (auth resolves
    // mid-render, or the user signs in while the modal is still
    // open). Peer-review 2026-09-28 MEDIUM. Without this, a fast
    // signup flow could leave a bottom sheet on top of the
    // signed-in UI until the user manually dismisses.
    if (!signedOut) {
      setVisible(false);
      return;
    }
    if (!venueId || !isNativeApp()) return;
    const state = readPreAuthPushState(venueId);
    if (shouldShowWelcomePrompt(state)) setVisible(true);
  }, [signedOut, venueId]);

  if (!visible) return null;

  async function onPrimary() {
    setBusy(true);
    try {
      let response: PreAuthPushResponse;
      try {
        const permission = await requestNativePushPermission();
        // Only an explicit OS "denied" is a permanent no. Any other
        // non-granted value (prompt, prompt-with-rationale) came
        // from a system that hasn't actually asked yet, so treat as
        // dismissed + retry after cool-off.
        response =
          permission === "granted"
            ? "granted"
            : permission === "denied"
              ? "denied"
              : "asked_dismissed";
      } catch {
        // Plugin missing, Capacitor not ready, transient OS error.
        // Peer-review 2026-09-28 LOW: an earlier version stored
        // "denied" here, which permanently silenced the welcome
        // prompt for anyone who hit a one-off plugin failure. Now
        // stored as "asked_dismissed" so the 7-day cool-off retries.
        response = "asked_dismissed";
      }
      writePreAuthPushState({ venueId, response });
    } finally {
      setVisible(false);
      setBusy(false);
    }
  }

  function onDismiss() {
    writePreAuthPushState({ venueId, response: "asked_dismissed" });
    setVisible(false);
  }

  const label = venueName || "us";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "color-mix(in srgb, black 55%, transparent)",
        zIndex: 9998,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-push-title"
    >
      <div
        style={{
          background: "var(--surface, #fff)",
          color: "var(--foreground, #111)",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: 24,
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
        }}
      >
        <h2
          id="welcome-push-title"
          style={{ margin: "0 0 8px 0", fontSize: 20, fontWeight: 700 }}
        >
          Get notified
        </h2>
        <p style={{ margin: "0 0 20px 0", fontSize: 15, lineHeight: 1.4 }}>
          Turn on notifications so you never miss when {label} announces gigs,
          drops offers, or confirms your booking.
        </p>
        <button
          type="button"
          className="subscribe-btn !font-normal"
          style={{ width: "100%", marginBottom: 8 }}
          disabled={busy}
          onClick={onPrimary}
        >
          {busy ? "…" : "Turn on notifications"}
        </button>
        <button
          type="button"
          style={{
            background: "transparent",
            border: 0,
            color: "var(--muted, #666)",
            width: "100%",
            padding: 12,
            fontSize: 14,
            cursor: "pointer",
          }}
          disabled={busy}
          onClick={onDismiss}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
